// ============================================================
// DockerCICDWorkflow.jsx
//
// A branded, responsive course content page for the
// "CI/CD Automation with GitHub Actions" lesson.
//
// Author: ProDev Linux Series (DevOps Branch)
// Features: Secret management, YAML workflow breakdown,
//           and navigation bridge to the Dashboard.
// ============================================================

import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";

// ── MUI Layout & Display ──────────────────────────────────
import {
    Box,
    Container,
    Typography,
    Grid,
    Chip,
    Divider,
    Tooltip,
    Paper,
    Fab,
    Zoom,
    Stack,
    Avatar,
    Button
} from "@mui/material";

// ── MUI Icons ────────────────────────────────────────────
import SettingsSuggestIcon    from "@mui/icons-material/SettingsSuggest";
import PlayCircleIcon         from "@mui/icons-material/PlayCircle";
import SecurityIcon           from "@mui/icons-material/Security";
import TerminalIcon           from "@mui/icons-material/Terminal";
import LightbulbIcon          from "@mui/icons-material/Lightbulb";
import ContentCopyIcon        from "@mui/icons-material/ContentCopy";
import DoneIcon               from "@mui/icons-material/Done";
import SchoolIcon             from "@mui/icons-material/School";
import StarIcon               from "@mui/icons-material/Star";
import KeyboardArrowUpIcon    from "@mui/icons-material/KeyboardArrowUp";
import ArrowBackIcon          from "@mui/icons-material/ArrowBack";
import CodeIcon               from "@mui/icons-material/Code";
import GitHubIcon             from "@mui/icons-material/GitHub";

// ── Brand tokens ──────────────────────────────────────────
const BRAND = {
    bg:          "#233D4D",
    surface:     "rgba(255,255,255,0.04)",
    border:      "rgba(218,165,32,0.25)",
    borderHover: "rgba(218,165,32,0.6)",
    gold:        "#DAA520",
    goldBright:  "#FFD700",
    goldGlow:    "rgba(218,165,32,0.15)",
    white:       "#FFFFFF",
    muted:       "rgba(255,255,255,0.55)",
    codeBg:      "rgba(0,0,0,0.45)",
    codeText:    "#FFD700",
};

const sectionHeading = {
    fontFamily: "'Georgia', serif",
    fontWeight: 800,
    color: BRAND.white,
    letterSpacing: "-0.5px",
};

// ── Sub-Components ────────────────────────────────────────

function SectionTitle({ icon, label }) {
    return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2.5 }}>
            <Box sx={{
                width: 38, height: 38, borderRadius: "50%",
                background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`,
                display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
                {React.cloneElement(icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
            </Box>
            <Typography variant="h6" sx={{ ...sectionHeading, fontSize: { xs: 17, md: 19 } }}>
                {label}
            </Typography>
        </Box>
    );
}

function CodeBlock({ lines }) {
    const [copied, setCopied] = useState(false);
    const fullText = lines.map((l) => l.cmd).join("\n");

    const handleCopy = () => {
        navigator.clipboard.writeText(fullText).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    return (
        <Box sx={{ background: BRAND.codeBg, border: `1px solid ${BRAND.border}`, borderRadius: 2, overflow: "hidden", mb: 2 }}>
            <Box sx={{ px: 2, py: 1, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${BRAND.border}`, background: "rgba(0,0,0,0.3)" }}>
                <Box sx={{ display: "flex", gap: 0.7 }}>
                    {["#ff5f56", "#ffbd2e", "#27c93f"].map((c) => (
                        <Box key={c} sx={{ width: 10, height: 10, borderRadius: "50%", background: c }} />
                    ))}
                </Box>
                <Tooltip title={copied ? "Copied!" : "Copy code"} placement="top">
                    <Box onClick={handleCopy} sx={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 0.5, color: copied ? BRAND.goldBright : BRAND.muted, fontSize: 12 }}>
                        {copied ? <DoneIcon sx={{ fontSize: 15 }} /> : <ContentCopyIcon sx={{ fontSize: 15 }} />}
                        <span style={{ fontFamily: "monospace" }}>{copied ? "Copied" : "Copy"}</span>
                    </Box>
                </Tooltip>
            </Box>
            <Box sx={{ p: 2.5, display: "flex", flexDirection: "column", gap: 1 }}>
                {lines.map(({ cmd, comment }, i) => (
                    <Box key={i}>
                        {comment && <Typography sx={{ fontFamily: "monospace", fontSize: 12, color: "rgba(255,255,255,0.3)", mb: 0.2 }}># {comment}</Typography>}
                        <Typography sx={{ fontFamily: "monospace", fontSize: { xs: 13, md: 14 }, color: BRAND.codeText, whiteSpace: "pre-wrap" }}>{cmd}</Typography>
                    </Box>
                ))}
            </Box>
        </Box>
    );
}

// ── Main Component ────────────────────────────────────────

export default function DockerCICDWorkflow() {
    const [showScroll, setShowScroll] = useState(false);

    useEffect(() => {
        const checkScroll = () => {
            if (!showScroll && window.pageYOffset > 400) setShowScroll(true);
            else if (showScroll && window.pageYOffset <= 400) setShowScroll(false);
        };
        window.addEventListener("scroll", checkScroll);
        return () => window.removeEventListener("scroll", checkScroll);
    }, [showScroll]);

    return (
        <Box sx={{ minHeight: "100vh", background: BRAND.bg, py: { xs: 4, md: 7 }, px: { xs: 2, sm: 3 },
            backgroundImage: `repeating-linear-gradient(-45deg, rgba(218,165,32,0.02) 0px, rgba(218,165,32,0.02) 1px, transparent 1px, transparent 12px)`
        }}>
            <Container maxWidth="lg">
                {/* HERO HEADER */}
                <Box sx={{ mb: { xs: 5, md: 7 }, textAlign: { xs: "center", md: "left" } }}>
                    <Chip icon={<SchoolIcon sx={{ color: `${BRAND.gold} !important`, fontSize: 15 }} />} label="Automation · Module 32"
                          sx={{ mb: 2.5, background: BRAND.goldGlow, border: `1px solid ${BRAND.border}`, color: BRAND.gold, fontFamily: "monospace", fontSize: 11 }} />
                    <Typography variant="h3" sx={{ ...sectionHeading, fontSize: { xs: 28, sm: 36, md: 44 }, mb: 2 }}>
                        Automating <span>Docker Workflows</span>
                    </Typography>
                    <Typography sx={{ color: BRAND.muted, fontSize: { xs: 14, md: 16 }, maxWidth: 620, lineHeight: 1.75, mx: { xs: "auto", md: 0 } }}>
                        Manual pushes are a thing of the past. By leveraging GitHub Actions, we can automate the entire build, tag, and publish lifecycle, ensuring your production registry is always in sync with your latest code.
                    </Typography>
                </Box>

                {/* 1. SECRETS MANAGEMENT */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SecurityIcon />} label="1. Configuring GitHub Secrets" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Never hardcode credentials in your YAML files. Store your registry tokens securely in the repository settings.
                    </Typography>
                    <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2 }}>
                        <Typography sx={{ color: BRAND.gold, fontWeight: 700, fontSize: 14, mb: 1 }}>Required Secrets:</Typography>
                        <Stack spacing={1}>
                            <Typography sx={{ color: BRAND.white, fontSize: 13 }}>• <code style={{color: BRAND.goldBright}}>DOCKER_USERNAME</code>: Registry username (Docker Hub/GHCR)</Typography>
                            <Typography sx={{ color: BRAND.white, fontSize: 13 }}>• <code style={{color: BRAND.goldBright}}>DOCKER_PASSWORD</code>: Personal Access Token (PAT)</Typography>
                        </Stack>
                        <Typography sx={{ color: BRAND.muted, fontSize: 11, mt: 2, fontStyle: "italic" }}>
                            Navigation: Repo → Settings → Secrets & Variables → Actions
                        </Typography>
                    </Paper>
                </Box>

                {/* 2. THE YAML WORKFLOW */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<CodeIcon />} label="2. The Workflow Definition" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Place this configuration in <code style={{color: BRAND.white}}>.github/workflows/docker-publish.yml</code> to trigger automation on every push to main.
                    </Typography>
                    <CodeBlock lines={[
                        { cmd: "name: Build & Push Docker Image", comment: "" },
                        { cmd: "on:", comment: "" },
                        { cmd: "  push:", comment: "" },
                        { cmd: "    branches: [main]", comment: "Trigger on main branch updates" },
                        { cmd: "jobs:", comment: "" },
                        { cmd: "  docker:", comment: "" },
                        { cmd: "    runs-on: ubuntu-latest", comment: "" },
                        { cmd: "    steps:", comment: "" },
                        { cmd: "      - uses: actions/checkout@v3", comment: "Get source code" },
                        { cmd: "      - uses: docker/login-action@v2", comment: "Authenticate with Registry" },
                        { cmd: "        with:", comment: "" },
                        { cmd: "          username: ${{ secrets.DOCKER_USERNAME }}", comment: "" },
                        { cmd: "          password: ${{ secrets.DOCKER_PASSWORD }}", comment: "" },
                        { cmd: "      - uses: docker/build-push-action@v5", comment: "Build and Push" },
                        { cmd: "        with:", comment: "" },
                        { cmd: "          push: true", comment: "" },
                        { cmd: "          tags: username/my-app:latest", comment: "" },
                    ]} />
                </Box>

                {/* 3. VERIFICATION */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<PlayCircleIcon />} label="3. Monitoring the CI Pipeline" />
                    <Typography sx={{ color: BRAND.muted, mb: 2, fontSize: 14 }}>
                        Once the code is pushed, navigate to the <strong>Actions</strong> tab in your GitHub repository to watch the build steps in real-time.
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                        <Chip label="Build Status: Passing" color="success" variant="outlined" sx={{ borderRadius: 1 }} />
                        <Chip label="Runner: Ubuntu-Latest" variant="outlined" sx={{ borderRadius: 1, color: BRAND.muted, borderColor: BRAND.border }} />
                    </Box>
                </Box>

                <Divider sx={{ borderColor: BRAND.border, mb: 6 }} />

                {/* BEST PRACTICES */}
                <Box sx={{ mb: 6 }}>
                    <SectionTitle icon={<SettingsSuggestIcon />} label="Deployment Strategies" />
                    <Grid container spacing={2}>
                        {[
                            { title: "Semantic Versioning", desc: "Use tags like :v1.0.1 instead of :latest for predictable production rollbacks.", icon: <StarIcon /> },
                            { title: "Registry Choice", desc: "Switching to GHCR? Just update the tags to ghcr.io/username/app-name.", icon: <GitHubIcon /> },
                            { title: "Environment Injection", desc: "Use --build-arg to pass non-sensitive build configs into the container.", icon: <TerminalIcon /> },
                        ].map((item) => (
                            <Grid item xs={12} md={4} key={item.title}>
                                <Paper sx={{ p: 2.5, background: BRAND.surface, border: `1px solid ${BRAND.border}`, borderRadius: 2, height: '100%' }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                        {React.cloneElement(item.icon, { sx: { color: BRAND.gold, fontSize: 20 } })}
                                        <Typography sx={{ color: BRAND.white, fontWeight: 700, fontSize: 14 }}>{item.title}</Typography>
                                    </Box>
                                    <Typography sx={{ color: BRAND.muted, fontSize: 12 }}>{item.desc}</Typography>
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                {/* FOOTER ACTIONS */}
                <Box sx={{ textAlign: "center", py: 4 }}>
                    <Button
                        component={Link}
                        to="/express-dashboard"
                        variant="outlined"
                        startIcon={<ArrowBackIcon />}
                        sx={{
                            borderColor: BRAND.gold,
                            color: BRAND.gold,
                            borderRadius: "999px",
                            px: 4,
                            fontWeight: 800,
                            "&:hover": {
                                borderColor: BRAND.goldBright,
                                background: BRAND.goldGlow
                            }
                        }}
                    >
                        Back to Express Dashboard
                    </Button>
                </Box>

                <Box sx={{ mt: 2, p: 2.5, border: `1px solid ${BRAND.border}`, borderRadius: 2, background: BRAND.goldGlow, display: "flex", alignItems: "center", gap: 1.5 }}>
                    <LightbulbIcon sx={{ color: BRAND.gold }} />
                    <Typography sx={{ color: BRAND.muted, fontSize: 13 }}>
                        <strong style={{ color: BRAND.white }}>Pro Tip:</strong> For faster builds, use the <code style={{ color: BRAND.goldBright }}>docker/setup-buildx-action</code> in your workflow. This enables advanced caching features that significantly reduce build times for subsequent runs.
                    </Typography>
                </Box>
            </Container>

            {/* ✅ Floating Scroll-to-Top */}
            <Stack spacing={2} sx={{ position: "fixed", bottom: 25, right: 25, zIndex: 1000 }}>
                <Zoom in={showScroll}>
                    <Fab size="small" onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} sx={{ bgcolor: BRAND.gold, "&:hover": { bgcolor: BRAND.goldBright } }}>
                        <KeyboardArrowUpIcon sx={{ color: "#100720" }} />
                    </Fab>
                </Zoom>
            </Stack>
        </Box>
    );
}
